import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Heart, Flame, Clock, Volume2, Trophy, Star, XCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMER_SECONDS = 30;
const MAX_LIVES = 3;
const TOTAL_QUESTIONS = 18;
const STREAK_TO_LEVEL_UP = 3;

const DIFF_LABELS = { easy: "Easy", medium: "Medium", hard: "Hard" };
const DIFF_COLORS = {
  easy: "bg-[#3C8DE4] text-white",
  medium: "bg-[#E8A838] text-white",
  hard: "bg-[#E85A6E] text-white",
};

// Accent-insensitive, case/punctuation/whitespace-insensitive answer matching —
// mirrors the backend's grading logic in POST /api/assignments/submit.
const stripAccents = (str) => (str || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const normalizeKeepAccents = (str) =>
  (str || "")
    .trim()
    .toLowerCase()
    .replace(/[!"#$%&'()*+,\-./:;<=>?@[\]^_`{|}~]/g, "")
    .replace(/\s+/g, "");
const normalizeAnswer = (str) => stripAccents(normalizeKeepAccents(str));

const ElephantMascot = ({ mood }) => (
  <svg viewBox="0 0 200 220" className="w-40 h-40 mx-auto drop-shadow-lg">
    {/* Left Ear */}
    <path d="M72 70 C58 40, 12 28, 10 65 C8 96, 35 116, 60 100" fill="#B8D4E8" stroke="#0A0A0A" strokeWidth="2.5"/>
    <path d="M65 70 C55 48, 24 40, 22 65 C20 86, 40 102, 58 92" fill="#F5CEBD"/>
    {/* Right Ear */}
    <path d="M128 70 C142 40, 188 28, 190 65 C192 96, 165 116, 140 100" fill="#B8D4E8" stroke="#0A0A0A" strokeWidth="2.5"/>
    <path d="M135 70 C145 48, 176 40, 178 65 C180 86, 160 102, 142 92" fill="#F5CEBD"/>
    {/* Head */}
    <circle cx="100" cy="80" r="48" fill="#B8D4E8" stroke="#0A0A0A" strokeWidth="2.5"/>
    {/* Eyes */}
    {mood === "happy" ? (
      <>
        <path d="M80 72 Q85 66 90 72" stroke="#0A0A0A" strokeWidth="3" fill="none" strokeLinecap="round"/>
        <path d="M110 72 Q115 66 120 72" stroke="#0A0A0A" strokeWidth="3" fill="none" strokeLinecap="round"/>
      </>
    ) : mood === "sad" ? (
      <>
        <ellipse cx="85" cy="72" rx="8" ry="9" fill="#0A0A0A"/>
        <circle cx="82" cy="69" r="2.5" fill="white"/>
        <ellipse cx="115" cy="72" rx="8" ry="9" fill="#0A0A0A"/>
        <circle cx="112" cy="69" r="2.5" fill="white"/>
        <path d="M76 86 Q82 92 88 86" stroke="#0A0A0A" strokeWidth="2" fill="none" strokeLinecap="round"/>
      </>
    ) : (
      <>
        <ellipse cx="85" cy="72" rx="8" ry="10" fill="#0A0A0A"/>
        <circle cx="82" cy="68" r="3" fill="white"/>
        <ellipse cx="115" cy="72" rx="8" ry="10" fill="#0A0A0A"/>
        <circle cx="112" cy="68" r="3" fill="white"/>
      </>
    )}
    {/* Cheeks */}
    <circle cx="67" cy="84" r="8" fill="#FFB6C1" opacity="0.45"/>
    <circle cx="133" cy="84" r="8" fill="#FFB6C1" opacity="0.45"/>
    {/* Trunk */}
    <ellipse cx="100" cy="88" rx="8" ry="10" fill="#B8D4E8" stroke="#0A0A0A" strokeWidth="2"/>
    <ellipse cx="100" cy="93" rx="5" ry="4" fill="#FFD6DC"/>
    {/* Smile or frown */}
    {mood === "sad" ? (
      <path d="M90 102 Q100 96 110 102" stroke="#0A0A0A" strokeWidth="2" fill="none" strokeLinecap="round"/>
    ) : (
      <path d="M88 100 Q100 110 112 100" stroke="#0A0A0A" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
    )}
    {/* Body */}
    <path d="M74 125 C74 116, 82 112, 100 112 C118 112, 126 116, 126 125 L126 170 C126 180, 118 185, 100 185 C82 185, 74 180, 74 170 Z" fill="#B8D4E8" stroke="#0A0A0A" strokeWidth="2.5"/>
    <ellipse cx="100" cy="150" rx="18" ry="14" fill="#E8F4FC"/>
    {/* Feet */}
    <ellipse cx="82" cy="188" rx="12" ry="7" fill="#B8D4E8" stroke="#0A0A0A" strokeWidth="2"/>
    <ellipse cx="118" cy="188" rx="12" ry="7" fill="#B8D4E8" stroke="#0A0A0A" strokeWidth="2"/>
    {/* Graduation cap */}
    {mood === "happy" && (
      <>
        <polygon points="100,20 65,38 100,48 135,38" fill="#2D3748"/>
        <rect x="82" y="40" width="36" height="5" fill="#2D3748"/>
        <circle cx="100" cy="20" r="4" fill="#2D3748"/>
        <line x1="130" y1="37" x2="140" y2="52" stroke="#FFD700" strokeWidth="2"/>
        <circle cx="142" cy="55" r="4" fill="#FFD700"/>
      </>
    )}
  </svg>
);

const GamePage = () => {
  const { enrollmentId, weekNumber } = useParams();
  const [phase, setPhase] = useState("loading"); // loading, playing, ended
  const [questionPools, setQuestionPools] = useState({ easy: [], medium: [], hard: [] });
  const [gameQuestions, setGameQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackCorrect, setFeedbackCorrect] = useState(false);
  const [feedbackAnswer, setFeedbackAnswer] = useState("");
  const [accentReminder, setAccentReminder] = useState(null);
  const [lives, setLives] = useState(MAX_LIVES);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [timer, setTimer] = useState(TIMER_SECONDS);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [currentDifficulty, setCurrentDifficulty] = useState("easy");
  const [sessionId, setSessionId] = useState(null);
  const [shakeWrong, setShakeWrong] = useState(false);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  // Pool indices track which questions we've used from each difficulty
  const poolIndicesRef = useRef({ easy: 0, medium: 0, hard: 0 });

  useEffect(() => {
    loadQuestions();
    return () => clearInterval(timerRef.current);
  }, []);

  const loadQuestions = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${API}/assignments/game/${enrollmentId}/${weekNumber}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuestionPools(res.data);

      // Start anonymous session
      try {
        const sRes = await axios.post(`${API}/data/session/start`, {
          age_range: "unknown", grade_band: "unknown", skill_area: "French Game"
        });
        setSessionId(sRes.data.session_id);
      } catch { /* silent */ }

      // Build initial question sequence: start with easy
      const pools = res.data;
      poolIndicesRef.current = { easy: 0, medium: 0, hard: 0 };
      
      // Pick first question (easy)
      if (pools.easy.length > 0) {
        setGameQuestions([pools.easy[0]]);
        poolIndicesRef.current.easy = 1;
      }

      setPhase("playing");
      startTimer();
    } catch (err) {
      setPhase("error");
    }
  };

  const processAnswerRef = useRef(null);

  const startTimer = () => {
    setTimer(TIMER_SECONDS);
    startTimeRef.current = Date.now();
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          // Use ref to call latest processAnswer
          if (processAnswerRef.current) processAnswerRef.current(false, "");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleTimeUp = useCallback(() => {
    processAnswer(false, "");
  }, []);

  // Fix: handleTimeUp needs access to current state
    const elapsed = startTimeRef.current ? Math.round((Date.now() - startTimeRef.current) / 1000) : 0;
  useEffect(() => {
    // Update the timer callback with latest handleTimeUp
  }, [handleTimeUp]);

  const getNextQuestion = (wasCorrect, currentStreak) => {
    const pools = questionPools;
    let diff = currentDifficulty;

    // Adaptive: level up after STREAK_TO_LEVEL_UP correct in a row
    if (wasCorrect && currentStreak >= STREAK_TO_LEVEL_UP) {
      if (diff === "easy" && pools.medium.length > poolIndicesRef.current.medium) {
        diff = "medium";
      } else if (diff === "medium" && pools.hard.length > poolIndicesRef.current.hard) {
        diff = "hard";
      }
    }
    // Level down on wrong answer
    if (!wasCorrect) {
      if (diff === "hard") diff = "medium";
      else if (diff === "medium") diff = "easy";
    }

    setCurrentDifficulty(diff);

    // Pick next from the appropriate pool
    const poolOrder = [diff];
    if (diff === "easy") poolOrder.push("medium", "hard");
    else if (diff === "medium") poolOrder.push("easy", "hard");
    else poolOrder.push("medium", "easy");

    for (const d of poolOrder) {
      const pool = pools[d] || [];
      const idx = poolIndicesRef.current[d];
      if (idx < pool.length) {
        poolIndicesRef.current[d] = idx + 1;
        return pool[idx];
      }
    }

    return null; // No more questions
  };

  const processAnswer = (correct, answer) => {
    clearInterval(timerRef.current);

    const currentQ = gameQuestions[currentIndex];
    setFeedbackCorrect(correct);
    setFeedbackAnswer(currentQ?.correct_answer || answer);
    setShowFeedback(true);
    setAccentReminder(null);
    setTotalAnswered((p) => p + 1);
    setTotalTime((p) => p + elapsed);

    let newStreak = streak;
    let newLives = lives;

    if (correct) {
      newStreak = streak + 1;
      setStreak(newStreak);
      if (newStreak > bestStreak) setBestStreak(newStreak);
      setTotalCorrect((p) => p + 1);
    } else {
      newStreak = 0;
      setStreak(0);
      newLives = lives - 1;
      setLives(newLives);
      setShakeWrong(true);
      setTimeout(() => setShakeWrong(false), 500);
    }

    // Record anonymous attempt
    if (sessionId && currentQ) {
      axios.post(`${API}/data/question/attempt`, {
        session_id: sessionId,
        question_id: currentQ.id,
        attempt_number: 1,
        response_time_sec: elapsed,
        was_correct: correct,
        hint_used: false,
        skipped: !answer
      }).catch(() => {});
    }

    // Also submit to graded system
    if (currentQ) {
      const token = localStorage.getItem("access_token");
      axios.post(`${API}/assignments/submit`, {
        assignment_id: currentQ.id,
        enrollment_id: enrollmentId,
        user_answer: answer || "(timed out)",
        is_correct: false,
        time_taken_seconds: elapsed
      }, { headers: { Authorization: `Bearer ${token}` } }).then((res) => {
        if (res.data?.accent_reminder) {
          setAccentReminder(res.data.correct_answer_display || currentQ.correct_answer || "");
        }
      }).catch(() => {});
    }

    // After feedback, advance
    setTimeout(() => {
      setShowFeedback(false);
      setUserAnswer("");

      // Check end conditions
      if (newLives <= 0 || totalAnswered + 1 >= TOTAL_QUESTIONS) {
        endGame(newLives <= 0 ? "lives" : "complete");
        return;
      }

      // Get next question
      const next = getNextQuestion(correct, newStreak);
      if (!next) {
        endGame("complete");
        return;
      }

      setGameQuestions((prev) => [...prev, next]);
      setCurrentIndex((prev) => prev + 1);
      startTimer();
    }, correct ? 1200 : 2000);
  };

  const submitAnswer = () => {
    if (showFeedback) return;
    const currentQ = gameQuestions[currentIndex];
    if (!currentQ || !userAnswer.trim()) return;

    const userAns = normalizeAnswer(userAnswer);
    const correctAns = normalizeAnswer(currentQ.correct_answer || "");
    const isCorrect = userAns === correctAns;

    processAnswer(isCorrect, userAnswer);
  };

  // Keep ref in sync for timer callback
  processAnswerRef.current = processAnswer;

  const endGame = (reason) => {
    clearInterval(timerRef.current);
    setPhase("ended");

    // End anonymous session
    if (sessionId) {
      axios.post(`${API}/data/session/end`, {
        session_id: sessionId,
        completed: reason === "complete"
      }).catch(() => {});
    }
  };

  const playAudio = (text) => {
    if ("speechSynthesis" in window && text) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "fr-FR";
      u.rate = 0.8;
      window.speechSynthesis.speak(u);
    }
  };

  const getEndMessage = () => {
    const pct = totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;
    if (pct >= 90) return { text: "Magnifique!", sub: "You're a French superstar!", mood: "happy" };
    if (pct >= 70) return { text: "Tres bien!", sub: "Great job, keep it up!", mood: "happy" };
    if (pct >= 50) return { text: "Pas mal!", sub: "Not bad! Practice makes perfect.", mood: "neutral" };
    return { text: "Courage!", sub: "Keep practicing, you'll get there!", mood: "sad" };
  };

  // --- RENDER ---

  if (phase === "loading") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#3C8DE4] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white text-lg font-bold">Loading Week {weekNumber}...</p>
        </div>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-white text-center p-8">
        <div>
          <XCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
          <h2 className="text-2xl font-bold mb-2">Oops!</h2>
          <p className="text-white/70 mb-4">Couldn't load the quiz. Make sure this week is unlocked.</p>
          <button onClick={() => window.close()} className="brutalist-btn-primary">Go Back</button>
        </div>
      </div>
    );
  }

  if (phase === "ended") {
    const msg = getEndMessage();
    const pct = totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;
    const avgTime = totalAnswered > 0 ? Math.round(totalTime / totalAnswered) : 0;

    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0A0A] to-[#1a2a3a] flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center" data-testid="game-end-screen">
          <div className="mb-6 animate-bounce-slow">
            <ElephantMascot mood={msg.mood} />
          </div>

          <h1 className="text-5xl font-extrabold text-white mb-2">{msg.text}</h1>
          <p className="text-white/70 text-lg mb-8">{msg.sub}</p>

          <div className="grid grid-cols-2 gap-3 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="text-3xl font-extrabold text-white">{pct}%</div>
              <div className="text-xs text-white/60 uppercase tracking-wider">Accuracy</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="text-3xl font-extrabold text-white">{totalCorrect}/{totalAnswered}</div>
              <div className="text-xs text-white/60 uppercase tracking-wider">Correct</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="text-3xl font-extrabold text-[#FFD700]">
                <Flame className="w-6 h-6 inline mr-1" />{bestStreak}
              </div>
              <div className="text-xs text-white/60 uppercase tracking-wider">Best Streak</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="text-3xl font-extrabold text-white">{avgTime}s</div>
              <div className="text-xs text-white/60 uppercase tracking-wider">Avg Time</div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => window.location.reload()}
              data-testid="play-again-btn"
              className="flex-1 px-6 py-3 bg-[#3C8DE4] text-white font-bold rounded-xl border-2 border-white/30 hover:bg-[#2D7BD4] transition-colors"
            >
              Play Again
            </button>
            <button
              onClick={() => window.close()}
              data-testid="close-game-btn"
              className="flex-1 px-6 py-3 bg-white/10 text-white font-bold rounded-xl border-2 border-white/30 hover:bg-white/20 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- PLAYING PHASE ---
  const currentQ = gameQuestions[currentIndex];
  if (!currentQ) return null;

  const progress = Math.round(((currentIndex + 1) / TOTAL_QUESTIONS) * 100);
  const timerPct = (timer / TIMER_SECONDS) * 100;
  const timerColor = timer <= 5 ? "bg-red-500" : timer <= 10 ? "bg-yellow-500" : "bg-[#3C8DE4]";

  return (
    <div className={`min-h-screen bg-[#0A0A0A] text-white flex flex-col ${shakeWrong ? "animate-shake" : ""}`}>
      {/* Top Bar */}
      <div className="p-4 flex items-center justify-between border-b border-white/10" data-testid="game-top-bar">
        {/* Lives */}
        <div className="flex gap-1" data-testid="lives-display">
          {Array.from({ length: MAX_LIVES }).map((_, i) => (
            <Heart
              key={i}
              className={`w-6 h-6 transition-all ${i < lives ? "text-red-500 fill-red-500" : "text-white/20"}`}
            />
          ))}
        </div>

        {/* Progress */}
        <div className="flex-1 mx-6">
          <div className="h-3 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#3C8DE4] rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="text-[10px] text-white/50 text-center mt-1">
            {currentIndex + 1} / {TOTAL_QUESTIONS}
          </div>
        </div>

        {/* Streak */}
        <div className="flex items-center gap-1.5" data-testid="streak-display">
          <Flame className={`w-5 h-5 ${streak >= 3 ? "text-orange-400 animate-pulse" : "text-white/30"}`} />
          <span className={`font-bold text-lg ${streak >= 3 ? "text-orange-400" : "text-white/50"}`}>
            {streak}
          </span>
        </div>
      </div>

      {/* Timer Bar */}
      <div className="h-1.5 bg-white/5">
        <div
          className={`h-full ${timerColor} transition-all duration-1000 ease-linear`}
          style={{ width: `${timerPct}%` }}
          data-testid="timer-bar"
        />
      </div>

      {/* Question Area */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 max-w-2xl mx-auto w-full">
        {/* Difficulty Badge */}
        <div className="mb-4 flex items-center gap-3">
          <span className={`text-xs px-3 py-1 rounded-full font-bold ${DIFF_COLORS[currentQ.difficulty]}`}>
            {DIFF_LABELS[currentQ.difficulty]}
          </span>
          <div className="flex items-center gap-1 text-white/40 text-sm">
            <Clock className="w-4 h-4" />
            <span className={timer <= 5 ? "text-red-400 font-bold" : ""}>{timer}s</span>
          </div>
        </div>

        {/* Question */}
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 leading-snug" data-testid="game-question">
          {currentQ.question}
        </h2>

        {/* Audio Button */}
        {currentQ.audio_text && (
          <button
            onClick={() => playAudio(currentQ.audio_text)}
            data-testid="game-audio-btn"
            className="mb-6 flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full border border-white/20 hover:bg-white/20 transition-colors"
          >
            <Volume2 className="w-4 h-4" /> Listen
          </button>
        )}

        {/* MCQ Options */}
        {currentQ.type === "mcq" && currentQ.options ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
            {currentQ.options.map((opt, i) => (
              <button
                key={i}
                data-testid={`game-option-${i}`}
                onClick={() => {
                  if (showFeedback) return;
                  setUserAnswer(opt);
                }}
                disabled={showFeedback}
                className={`p-4 rounded-xl border-2 text-left font-medium transition-all ${
                  showFeedback && normalizeAnswer(opt) === normalizeAnswer(currentQ.correct_answer || "")
                    ? "border-green-400 bg-green-500/20 text-green-300"
                    : showFeedback && userAnswer === opt && !feedbackCorrect
                    ? "border-red-400 bg-red-500/20 text-red-300"
                    : userAnswer === opt
                    ? "border-[#3C8DE4] bg-[#3C8DE4]/20 text-white"
                    : "border-white/20 bg-white/5 text-white/90 hover:bg-white/10 hover:border-white/40"
                }`}
              >
                <span className="text-xs text-white/40 mr-2">{String.fromCharCode(65 + i)}.</span>
                {opt}
              </button>
            ))}
          </div>
        ) : (
          /* Text Input */
          <div className="w-full max-w-md">
            <input
              type="text"
              data-testid="game-text-input"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !showFeedback && submitAnswer()}
              placeholder="Type your answer..."
              disabled={showFeedback}
              autoFocus
              className="w-full px-6 py-4 bg-white/10 border-2 border-white/20 rounded-xl text-white text-lg text-center placeholder-white/30 focus:border-[#3C8DE4] focus:outline-none transition-colors"
            />
          </div>
        )}

        {/* Submit Button (for text and MCQ) */}
        {!showFeedback && (
          <button
            onClick={submitAnswer}
            disabled={!userAnswer}
            data-testid="game-submit-btn"
            className={`mt-6 px-10 py-3 rounded-xl font-bold text-lg transition-all ${
              userAnswer
                ? "bg-[#3C8DE4] text-white hover:bg-[#2D7BD4] border-2 border-[#3C8DE4]"
                : "bg-white/5 text-white/30 border-2 border-white/10 cursor-not-allowed"
            }`}
          >
            Check
          </button>
        )}

        {/* Feedback Overlay */}
        {showFeedback && (
          <div
            data-testid="game-feedback"
            className={`mt-6 px-8 py-4 rounded-xl text-center font-bold text-lg ${
              feedbackCorrect
                ? "bg-green-500/20 border-2 border-green-400 text-green-300"
                : "bg-red-500/20 border-2 border-red-400 text-red-300"
            }`}
          >
            {feedbackCorrect ? (
              <span>Correct! {streak >= 3 && <Flame className="w-5 h-5 inline text-orange-400" />}</span>
            ) : (
              <span>
                {timer === 0 ? "Time's up!" : "Wrong!"}{" "}
                <span className="text-white/70 font-normal text-base block mt-1">
                  Answer: <strong className="text-white">{feedbackAnswer}</strong>
                </span>
              </span>
            )}
          </div>
        )}

        {/* Accent reminder — informational only, dismissible, never blocks progress */}
        {accentReminder && (
          <div
            data-testid="accent-reminder-popup"
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-[#FBE7C6] text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-xl px-4 py-3 shadow-lg max-w-md"
          >
            <span className="text-sm">
              Correct! Just a reminder: the proper spelling uses accents — <strong>{accentReminder}</strong>.
            </span>
            <button
              onClick={() => setAccentReminder(null)}
              data-testid="dismiss-accent-reminder"
              className="shrink-0 text-[#0A0A0A]/60 hover:text-[#0A0A0A]"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        .animate-shake { animation: shake 0.5s ease-in-out; }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow { animation: bounce-slow 2s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default GamePage;
